/**
 * Pure logic for AdjustmentsModal commit branching. Extracted from
 * `EditorPage.handleAdjustmentCommit` so the destructive-vs-edit-mode
 * decision is unit-testable without rendering the full editor.
 *
 * The modal can fire `onCommit` in two distinct cases:
 *
 *  - **Edit mode** (`editingAdjustment` non-null): user is editing an
 *    existing adjustment-layer node via its config sliders. Pixels on
 *    the source layer are NOT touched — the slow-path compositor
 *    re-renders the adjustment effect on every slider tick. No
 *    rasterModified dispatch.
 *
 *  - **Destructive apply** (`editingAdjustment` null AND the modal
 *    body wrote pixels into each target's surface): the slider values
 *    were baked into the source layer's raster. For PSD round-trip,
 *    every affected layer's `rasterModified` flag must flip so the
 *    export path emits the modified raster rather than restoring the
 *    pre-edit PSD bytes from the source-stash.
 *
 * The third case — "Apply as adjustment layer" (non-destructive new
 * layer creation) — routes through `onCreateAdjustmentLayer`, NOT
 * `onCommit`, so it never reaches this helper.
 */

/** Minimal Fabric object shape needed for the layerId lookup. We
 *  deliberately don't import `fabric.Object` here so the helper can
 *  be tested with plain duck-typed objects (no Fabric init needed). */
interface FabricLikeObject {
  layerId?: string;
}

interface EditingAdjustment {
  layerId: string;
}

/** Returns the set of layer ids whose `rasterModified` flag should
 *  flip to true on adjustment commit, given the modal's commit-time
 *  context.
 *
 *  - Edit mode → empty array (no destructive bake happened).
 *  - Destructive → unique `layerId` values from the supplied
 *    Fabric-like targets, dropping any object that doesn't carry a
 *    layerId tag (defensive — should never happen in production but
 *    we don't want to dispatch `undefined`).
 *
 *  Returns a NEW array; the input is not mutated. Order matches input
 *  order with duplicates removed (first-occurrence wins). */
export function getDestructiveAdjustmentLayerIds(
  targets: readonly FabricLikeObject[],
  editingAdjustment: EditingAdjustment | null,
): string[] {
  if (editingAdjustment) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const obj of targets) {
    const id = obj.layerId;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}
